

import type { HeroState } from "@/core";
import type { Stats, EquipmentSlot } from "./types";
import { EQUIPMENT_CATALOG } from "./equipment";

const BASE_MAX_HP = 30;
const CONSTITUTION_TO_MAX_HP = 5;
const NO_BODY_ARMOR_EVASION_BONUS = 3;

export function statMod(stat: number | undefined): number {
  return stat ?? 0;
}


export function computeEffectiveStats(hero: Pick<HeroState, "baseStats" | "equipment">): Stats {
  const stats: Stats = { ...hero.baseStats };
  for (const itemId of Object.values(hero.equipment)) {
    if (!itemId) continue;
    const item = EQUIPMENT_CATALOG[itemId];
    if (!item) continue;
    for (const [stat, bonus] of Object.entries(item.statBonuses)) {
      stats[stat as keyof Stats] = (stats[stat as keyof Stats] ?? 0) + (bonus ?? 0);
    }
  }
  return stats;
}

export function computeEffectiveArmor(hero: Pick<HeroState, "equipment">): number {
  let armor = 0;
  for (const itemId of Object.values(hero.equipment)) {
    if (!itemId) continue;
    const item = EQUIPMENT_CATALOG[itemId];
    if (item) armor += item.armor;
  }
  return Math.max(0, armor);
}

export function computeEffectiveEvasion(hero: Pick<HeroState, "baseStats" | "equipment">): number {
  const effStats = computeEffectiveStats(hero);
  let evasion = statMod(effStats.dexterity);
  if (!hero.equipment.body) evasion += NO_BODY_ARMOR_EVASION_BONUS;
  for (const itemId of Object.values(hero.equipment)) {
    if (!itemId) continue;
    const item = EQUIPMENT_CATALOG[itemId];
    if (item) evasion += item.evasionMod;
  }
  return Math.max(1, evasion);
}

export function computeMaxHp(hero: Pick<HeroState, "baseStats" | "equipment">): number {
  const effStats = computeEffectiveStats(hero);
  return BASE_MAX_HP + statMod(effStats.constitution) * CONSTITUTION_TO_MAX_HP;
}

export function computeCritBonusPct(hero: Pick<HeroState, "equipment">): number {
  let pct = 0;
  for (const itemId of Object.values(hero.equipment)) {
    if (!itemId) continue;
    const item = EQUIPMENT_CATALOG[itemId];
    if (item?.critBonusPct) pct += item.critBonusPct;
  }
  return pct;
}

export function computeCritThreshold(hero: Pick<HeroState, "equipment">): number {
  const pct = computeCritBonusPct(hero);
  return Math.max(2, 20 - Math.floor(pct / 5));
}


export function computeEquipmentCards(hero: Pick<HeroState, "equipment">): string[] {
  const cards: string[] = [];
  for (const itemId of Object.values(hero.equipment)) {
    if (!itemId) continue;
    const item = EQUIPMENT_CATALOG[itemId];
    if (item) cards.push(...item.grantsCards);
  }
  return cards;
}


export function recomputeHeroDerived(hero: HeroState): void {
  hero.stats = computeEffectiveStats(hero);
  hero.armor = computeEffectiveArmor(hero);
  hero.evasion = computeEffectiveEvasion(hero);
  hero.maxHp = computeMaxHp(hero);
  hero.hp = Math.min(hero.hp, hero.maxHp);
}


export const SLOT_ORDER: EquipmentSlot[] = ["head", "body", "hands", "weapon", "accessory"];


export function slotLabel(slot: EquipmentSlot): string {
  return {
    head: "Голова",
    body: "Тело",
    hands: "Руки",
    weapon: "Оружие",
    accessory: "Аксессуар",
  }[slot];
}
